import { useMemo, useState } from 'react';
import { Box, Typography, Divider, Collapse, IconButton } from '@mui/material';
import Check from '@mui/icons-material/Check';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { PermissionGroup, Permission } from '@/types/identity/Identity';
import { UNSAVED_SX } from '@/lib/gridStyles';

interface GrantedPermissionsOverviewProps {
  groups: PermissionGroup[];
  /** The live grant state the editing tree holds — pending changes included, so the overview follows every click. */
  grants: Map<string, boolean>;
  /** Parent permission name → child permission names (as the editing tree builds it). */
  childrenMap: Map<string, string[]>;
  /** Grants that differ from the server's saved state — marked yellow until the save lands. */
  unsavedPerms: Set<string>;
}

/**
 * Read-only mirror of what the role actually holds: only the granted permissions, in their
 * group/parent hierarchy. Rendered from the editing tree's live grant state, so checking a
 * permission on the left shows it here immediately — marked with the app-standard unsaved
 * yellow until it is saved. An ungranted ancestor of a granted permission is shown muted, as
 * context for the indentation. Groups collapse like the editing tree's.
 */
export function GrantedPermissionsOverview({ groups, grants, childrenMap, unsavedPerms }: GrantedPermissionsOverviewProps) {
  const { t } = useTranslation();
  // Collapsed is the exception for an overview, so the state tracks collapsed groups.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Granted permissions plus their ancestors (the context rows). Ancestors resolve within the
  // owning group, the same walk the editing tree's filter does.
  const visiblePerms = useMemo(() => {
    const visible = new Set<string>();
    for (const group of groups) {
      for (const perm of group.permissions) {
        if (!grants.get(perm.name)) continue;
        visible.add(perm.name);
        let current = perm;
        while (current.parentName) {
          visible.add(current.parentName);
          const parent = group.permissions.find((p) => p.name === current.parentName);
          if (!parent) break;
          current = parent;
        }
      }
    }
    return visible;
  }, [groups, grants]);

  const grantedTotal = useMemo(() => {
    let count = 0;
    for (const group of groups) {
      for (const perm of group.permissions) {
        if (grants.get(perm.name)) count++;
      }
    }
    return count;
  }, [groups, grants]);

  const toggleCollapsed = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  const visibleGroups = groups.filter((g) => g.permissions.some((p) => visiblePerms.has(p.name)));

  return (
    <Box component="section" aria-label={t('identity.grantedPermissions')}>
      <Typography variant="subtitle2" gutterBottom>
        {t('identity.grantedPermissions')} ({grantedTotal})
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {grantedTotal === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('identity.noGrantedPermissions')}
        </Typography>
      )}
      {visibleGroups.map((group) => {
        const expanded = !collapsedGroups.has(group.name);
        return (
          <Box key={group.name} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => toggleCollapsed(group.name)}
            >
              <IconButton size="small" sx={{ p: 0 }}>
                <ExpandMore
                  sx={{
                    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 150ms',
                    fontSize: '1.1rem',
                  }}
                />
              </IconButton>
              <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                {group.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {group.permissions.filter((p) => grants.get(p.name)).length}/{group.permissions.length}
              </Typography>
            </Box>
            <Collapse in={expanded} unmountOnExit>
              {group.permissions
                .filter((p) => !p.parentName && visiblePerms.has(p.name))
                .map((perm) => (
                  <GrantedNode
                    key={perm.name}
                    permission={perm}
                    group={group}
                    grants={grants}
                    childrenMap={childrenMap}
                    visiblePerms={visiblePerms}
                    unsavedPerms={unsavedPerms}
                    depth={0}
                  />
                ))}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

interface GrantedNodeProps {
  permission: Permission;
  group: PermissionGroup;
  grants: Map<string, boolean>;
  childrenMap: Map<string, string[]>;
  visiblePerms: Set<string>;
  unsavedPerms: Set<string>;
  depth: number;
}

function GrantedNode({ permission, group, grants, childrenMap, visiblePerms, unsavedPerms, depth }: GrantedNodeProps) {
  const isGranted = grants.get(permission.name) ?? false;
  const isUnsaved = isGranted && unsavedPerms.has(permission.name);
  const childPerms = (childrenMap.get(permission.name) ?? [])
    .map((name) => group.permissions.find((p) => p.name === name))
    .filter((p): p is Permission => !!p && visiblePerms.has(p.name));

  return (
    <Box sx={{ ml: depth * 2 }}>
      <Box
        data-unsaved={isUnsaved || undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.125,
          // Granted but not saved yet: the app-standard yellow (child grids' unsaved rows).
          ...(isUnsaved ? { ...UNSAVED_SX, borderRadius: 0.5, pl: 0.5 } : {}),
        }}
      >
        {isGranted ? (
          <Check color="success" sx={{ fontSize: '0.9rem' }} />
        ) : (
          // Context row: an ungranted ancestor shown only so its granted children indent sensibly.
          <Box sx={{ width: '0.9rem' }} />
        )}
        <Typography
          variant="body2"
          sx={isGranted ? undefined : { color: 'text.disabled' }}
        >
          {permission.displayName}
        </Typography>
      </Box>
      {childPerms.map((child) => (
        <GrantedNode
          key={child.name}
          permission={child}
          group={group}
          grants={grants}
          childrenMap={childrenMap}
          visiblePerms={visiblePerms}
          unsavedPerms={unsavedPerms}
          depth={depth + 1}
        />
      ))}
    </Box>
  );
}
